import { useEffect, useLayoutEffect, useState } from "react";

/*
 * Side by side (drawer | timeline) or stacked (Follow up above the drawer,
 * the timeline under it), by the CareIQ TAB's own width, never the screen's
 * (responsive audit 10-07): the tab's width depends on the chart's patient
 * panel (open 370 / 320 px, or the 88 px rail), the Orders gutter and split
 * screen, so one 1376 px iPad gave 990 px of tab with the panel open and
 * 1222 px with it shut, and a screen query picked the wrong layout for both.
 *
 * The width is measured on the CareIQ root (a ResizeObserver; the panel's
 * open / shut, a rotation or a split screen re-measure it) and the answer is
 * a class on .n3-layout (CareIQTabN3.css .n3-layout.is-stacked), because the
 * DOM moves too: stacked, the Follow up lead renders above the drawer. One
 * source of truth for both, so the CSS and the DOM can never disagree.
 *
 * Where nothing can be measured (no layout: jsdom; a tab not laid out yet)
 * the old screen query stands in.
 */

// side by side from this many px of tab: the drawer keeps 540 px or more beside the 320 px timeline
export const STACK_MIN = 880;
// the old screen query, only a stand-in where the tab cannot be measured
export const STACKED_FALLBACK_Q = "(max-width:1200px) and (orientation:portrait), (max-width:900px)";

export const stackedFor = (width) => width < STACK_MIN;

// the element's content-box width; the scrollbar is counted in, so a scrollbar
// that comes and goes with the layout can never flip it back and forth
export const contentWidth = (el) => {
  if (!el) return 0;
  const w = el.offsetWidth || 0;
  if (!w) return 0;
  let pad = 0;
  try {
    const cs = getComputedStyle(el);
    pad = ["paddingLeft", "paddingRight", "borderLeftWidth", "borderRightWidth"].reduce((n, k) => n + (parseFloat(cs[k]) || 0), 0);
  } catch (_) {
    pad = 0;
  }
  return Math.max(0, w - pad);
};

const readMedia = (query) => {
  try {
    return Boolean(window.matchMedia && window.matchMedia(query).matches);
  } catch (_) {
    return false;
  }
};

export function useStackedLayout(ref, fallbackQuery = STACKED_FALLBACK_Q) {
  const [media, setMedia] = useState(() => readMedia(fallbackQuery));
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    let mq;
    try {
      mq = window.matchMedia(fallbackQuery);
    } catch (_) {
      return undefined;
    }
    if (!mq) return undefined;
    const on = () => setMedia(Boolean(mq.matches));
    on();
    if (mq.addEventListener) mq.addEventListener("change", on);
    else if (mq.addListener) mq.addListener(on);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", on);
      else if (mq.removeListener) mq.removeListener(on);
    };
  }, [fallbackQuery]);

  // measured before the first paint (a layout effect), so the tab never flashes the other layout
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(contentWidth(el));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return width > 0 ? stackedFor(width) : media;
}
